import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Link } from '@tanstack/react-router';
import { FeatureSpec } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess, formatString } from '../../utils/helpers';
import { IntegrationsMetadata, RoleBasedValue, SupportedFeature } from '../../utils/typings';

interface SupportedFeaturesAccordionProps {
  integrationsMetadata?: IntegrationsMetadata;
  supportedFeatures?: FeatureSpec[];
  userRole?: keyof typeof UserRole;
}

/**
 * Type guard to check whether a value follows the RoleBasedValue shape.
 * This helps distinguish RBAC-configured fields from primitive values.
 */
const isRoleBasedValue = (value: RoleBasedValue): boolean =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Utility to determine whether a string should be rendered as a clickable link.
 */
const isHttpLink = (value: string): boolean => typeof value === 'string' && value.startsWith('http');

const SupportedFeaturesAccordion: React.FC<SupportedFeaturesAccordionProps> = ({
  integrationsMetadata,
  supportedFeatures = [],
  userRole,
}) => {
  /**
   * Memoized RBAC computation to avoid recalculating access rules on every render.
   */
  const { viewableFeatureKeys, hasSupportedFeaturesAccess } = useMemo(() => {
    // RBAC configuration for supported features section
    const rbacConfig = integrationsMetadata?.supportedFeatures ?? {};

    // Overall access check for the Supported Features section
    const hasAccess = checkRbacAccess(rbacConfig, userRole).hasViewAccess;

    /**
     * Compute per-feature visibility based on RBAC rules.
     * The result is a lookup map like: { description: true, helpArticleUrl: false }
     */
    const computedViewableFeatureKeys = Object.entries(rbacConfig).reduce<Record<string, boolean>>(
      (acc, [key, config]) => {
        acc[key] = isRoleBasedValue(config) ? checkRbacAccess(config, userRole).hasViewAccess : false;
        return acc;
      },
      {}
    );

    return {
      viewableFeatureKeys: computedViewableFeatureKeys,
      hasSupportedFeaturesAccess: hasAccess,
    };
  }, [integrationsMetadata, userRole]);

  /**
   * Early return when:
   * - No supported features exist
   * - User does not have RBAC access to view them
   */
  if (supportedFeatures.length === 0 || !hasSupportedFeaturesAccess) {
    return (
      <Accordion variant='main' showBoxShadow>
        <Accordion.Item value='supported-features'>
          <Accordion.Header>
            <Text weight='bold'>Supported Features</Text>
          </Accordion.Header>
          <Accordion.Body>
            <Text>No supported features available.</Text>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  }

  return (
    <Accordion variant='main' showBoxShadow>
      <Accordion.Item value='supported-features'>
        <Accordion.Header>
          <Text weight='bold'>Supported Features</Text>
        </Accordion.Header>

        <Accordion.Body>
          <Accordion variant='main' isMulti>
            {supportedFeatures.map((feature) => (
              <Accordion.Item key={feature.feature} value={feature.feature || ''}>
                <Accordion.Header>
                  <Text weight='bold' css={textAlignStyle}>
                    <Icon name='confirm-badge' color='success' />
                    {feature.feature}
                  </Text>
                </Accordion.Header>

                <Accordion.Body>
                  {Object.entries(feature)
                    .filter(([key]) => key !== 'feature' && viewableFeatureKeys[key as keyof SupportedFeature])
                    .map(([key, value]) => (
                      <div key={key} css={featureItemStyle}>
                        <Text as='div' css={featureItemContentStyle}>
                          {formatString(key)} :{' '}
                          {isHttpLink(value) ? (
                            <Link to={value} target='_blank' rel='noopener noreferrer'>
                              {value}
                            </Link>
                          ) : (
                            <span>{String(value)}</span>
                          )}
                        </Text>
                      </div>
                    ))}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default SupportedFeaturesAccordion;

const featureItemStyle = css`
  display: flex;

  &::before {
    content: '•';
    color: #555;
    margin-right: ${theme.spacing(0.5)};
  }
`;

const featureItemContentStyle = css`
  margin-bottom: ${theme.spacing(1.5)};
`;

const textAlignStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
`;
